/**
 * Karta produktu pre ŠABLÓNU "template_2".
 * Layout (obrázok + texty) ako template_1:
 *  - badgeOnImage=false (ZOZNAM): horizontálny riadok [číslo][obrázok][text][cena].
 *  - badgeOnImage=true  (MRIEŽKA): vertikálna karta – obrázok hore (číslo + cena na obrázku), text pod ním.
 * GLUTEN FREE odznak aj GRAMÁŽ (z productParameters.weightKg, prepočítaná na gramy):
 *  - landscape / mriežka: na obrázku (gluten-free vpravo hore, gramáž vľavo dole).
 *  - portrait (zoznam): vpravo pred cenou (inline); ak cena chýba, je tam sám.
 */
import React from "react";
import { resolveLocaleValue } from "app/utils/resolveLocaleValue";
import { formatAllergens } from "../../helpers/formatAllergens";
import { formatItemQuantity } from "../../helpers/formatItemQuantity";
import { formatPrice } from "../../helpers/formatPrice";
import GlutenFreeIcon from "./GlutenFreeIcon";

function isGlutenFree(item) {
  const v = item?.isGlutenFree ?? item?.glutenFree;
  return v === true || v === "true";
}

export default function Template2ProductCard({
  item,
  locale,
  defaultLocale,
  placeholderImage,
  showDescription = true,
  showPrices = true,
  badgeOnImage = false,
  orientation = "landscape",
}) {
  const imageUrl = item?.imageUrl || placeholderImage;
  const title = resolveLocaleValue(item?.title, locale, defaultLocale);
  const description = showDescription
    ? resolveLocaleValue(item?.description, locale, defaultLocale)
    : "";
  const priceDisplay = formatPrice(item?.price);
  const hasPrice = showPrices && priceDisplay !== "";
  const unavailable = item?.isActive === false;
  const allergensBrackets = formatAllergens(item?.allergens);
  const number = item?.ordinal ?? item?.number;
  const glutenFree = isGlutenFree(item);
  const weightDisplay = formatItemQuantity(item);

  // Portrait + zoznam: gluten-free aj gramáž idú vpravo pred cenu (nie na obrázok).
  const gfInline = !badgeOnImage && orientation === "portrait";

  const badge =
    number != null ? <span className="dm-t2__item-badge">{number}</span> : null;
  const gfBadge = glutenFree ? (
    <span
      className={`dm-t2__item-gf${gfInline ? " dm-t2__item-gf--inline" : ""}`}
      title="Bezlepkové"
    >
      <GlutenFreeIcon size="100%" strokeWidth={0} />
    </span>
  ) : null;
  const weightBadge = weightDisplay ? (
    <span
      className={`dm-t2__item-weight${gfInline ? " dm-t2__item-weight--inline" : ""}`}
    >
      {weightDisplay}
    </span>
  ) : null;

  const priceEl = hasPrice ? (
    <span className="dm-t2__item-price">{priceDisplay} €</span>
  ) : null;

  return (
    <div
      className={`dm-t2__item${unavailable ? " dm-t2__item--unavailable" : ""}${
        !showDescription ? " dm-t2__item--no-desc" : ""
      }`}
    >
      {badgeOnImage ? (
        <div className="dm-t2__item-image-wrap">
          <img className="dm-t2__item-image" src={imageUrl} alt="" />
          {badge}
          {gfBadge}
          {weightBadge}
          {hasPrice && (
            <span className="dm-t2__item-price-on-image">{priceDisplay} €</span>
          )}
        </div>
      ) : (
        <>
          {badge}
          <div className="dm-t2__item-media">
            <img className="dm-t2__item-image" src={imageUrl} alt="" />
            {/* na obrázku len keď nie je inline (landscape) */}
            {!gfInline && gfBadge}
            {!gfInline && weightBadge}
          </div>
        </>
      )}
      <div
        className={`dm-t2__item-body${
          !showDescription ? " dm-t2__item-body--no-desc" : ""
        }`}
      >
        <h3
          className={`dm-t2__item-title${
            !showDescription ? " dm-t2__item-title--full" : ""
          }`}
        >
          {title}
          {allergensBrackets && (
            <span className="dm-t2__item-allergens"> {allergensBrackets}</span>
          )}
        </h3>
        {showDescription && description && (
          <p className="dm-t2__item-description">{description}</p>
        )}
      </div>
      {!badgeOnImage &&
        (gfInline ? weightBadge || gfBadge || priceEl : priceEl) && (
          <div className="dm-t2__item-end">
            {gfInline && weightBadge}
            {gfInline && gfBadge}
            {priceEl}
          </div>
        )}
    </div>
  );
}
